import React, { useState, useEffect } from 'react';
import { Database, Zap, TrendingUp, Users, Activity, Globe, Clock, Server } from 'lucide-react';

interface DatabaseStats {
  status: string;
  collections: number;
  documents: number;
  latency: number;
  uptime: string;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DatabaseStats>({
    status: 'connecting',
    collections: 0,
    documents: 0,
    latency: 0,
    uptime: 'Checking...',
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate fetching database stats
    const fetchStats = async () => {
      try {
        // In a real app, this would call the EdgeVector API
        await new Promise(resolve => setTimeout(resolve, 1000));

        setStats({
          status: 'healthy',
          collections: 12,
          documents: 45832,
          latency: 3.2,
          uptime: '7d 4h 23m',
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Database Status',
      value: stats.status,
      icon: Database,
      color: stats.status === 'healthy' ? 'text-green-600' : 'text-red-600',
      bgColor: stats.status === 'healthy' ? 'bg-green-50' : 'bg-red-50',
    },
    {
      title: 'Total Collections',
      value: loading ? '...' : stats.collections.toString(),
      icon: Server,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Total Documents',
      value: loading ? '...' : stats.documents.toLocaleString(),
      icon: Database,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Avg Latency',
      value: loading ? '...' : `${stats.latency}ms`,
      icon: Zap,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
    {
      title: 'Active Users',
      value: loading ? '...' : '2,847',
      icon: Users,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
    },
    {
      title: 'Global Locations',
      value: loading ? '...' : '194',
      icon: Globe,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
  ];

  return (
    <div className='space-y-8'>
      {/* Hero Section */}
      <div className='text-center py-12'>
        <h1 className='text-4xl font-bold text-gray-900 mb-4'>EdgeVector Dashboard</h1>
        <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
          Welcome to the world's most advanced edge-native database platform. Experience
          MongoDB-compatible operations with AI-powered features and global distribution.
        </p>
        <div className='flex justify-center items-center space-x-4 mt-8'>
          <div className='bg-green-50 px-4 py-2 rounded-full flex items-center space-x-2'>
            <Activity className='w-5 h-5 text-green-600' />
            <span className='text-sm font-medium text-green-700'>Live Database Connection</span>
          </div>
          <div className='bg-blue-50 px-4 py-2 rounded-full flex items-center space-x-2'>
            <Clock className='w-5 h-5 text-blue-600' />
            <span className='text-sm font-medium text-blue-700'>{stats.uptime} uptime</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className='card hover:scale-105 transition-transform duration-200'>
              <div className='flex items-center justify-between'>
                <div>
                  <h3 className='text-lg font-semibold text-gray-900 mb-2'>{card.title}</h3>
                  <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${card.bgColor}`}>
                  <Icon className={`w-8 h-8 ${card.color}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Highlights */}
      <div className='grid md:grid-cols-2 gap-8'>
        {/* Core Features */}
        <div className='card'>
          <h3 className='text-2xl font-bold text-gray-900 mb-6 flex items-center'>
            <Database className='w-6 h-6 mr-2 text-blue-600' />
            Core Database Features
          </h3>
          <div className='space-y-4'>
            <div className='flex items-center space-x-3'>
              <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              <span className='text-gray-700'>MongoDB-compatible document operations</span>
            </div>
            <div className='flex items-center space-x-3'>
              <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              <span className='text-gray-700'>Vector search with AI embeddings</span>
            </div>
            <div className='flex items-center space-x-3'>
              <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              <span className='text-gray-700'>Time series analytics and metrics</span>
            </div>
            <div className='flex items-center space-x-3'>
              <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              <span className='text-gray-700'>Real-time streaming and subscriptions</span>
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div className='card'>
          <h3 className='text-2xl font-bold text-gray-900 mb-6 flex items-center'>
            <TrendingUp className='w-6 h-6 mr-2 text-green-600' />
            Performance Metrics
          </h3>
          <div className='space-y-4'>
            <div className='flex justify-between items-center'>
              <span className='text-gray-700'>Global Latency</span>
              <span className='font-semibold text-green-600'>{stats.latency}ms avg</span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-gray-700'>Query Throughput</span>
              <span className='font-semibold text-green-600'>45,000 QPS</span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-gray-700'>Edge Locations</span>
              <span className='font-semibold text-green-600'>194 active</span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-gray-700'>Data Consistency</span>
              <span className='font-semibold text-green-600'>99.999%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className='card text-center py-12'>
        <h3 className='text-2xl font-bold text-gray-900 mb-4'>Ready to Experience EdgeVector?</h3>
        <p className='text-gray-600 mb-6 max-w-2xl mx-auto'>
          Explore our interactive demo features including document operations, vector search, time
          series analytics, and real-time streaming capabilities.
        </p>
        <div className='flex justify-center space-x-4'>
          <button className='btn btn-primary'>
            <Database className='w-5 h-5' />
            Start Using Documents
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
